// ════════════════════════════════════════════════════════════
//  SHATTERPROOF — the app shell
// ════════════════════════════════════════════════════════════
//  Title → Team Select → the Shatterfield → Results.
//  The battle object lives in a ref for its whole life; React only
//  hears about screen changes, never about the fight itself.
//
//  Music: caP'n's own songs only (Terror's rule) — the login-menu
//  song on the title (the boot/login song every IE game borrows
//  until it has its own) and the Shatterfield song in the duel.
//  No sound effects yet: he makes those himself.
// ════════════════════════════════════════════════════════════
import { useCallback, useEffect, useRef, useState } from "react";
import "./App.css";
import BattleScene from "./three/BattleScene.jsx";
import { arenaFor } from "./three/Arena.jsx";
import Hud from "./ui/Hud.jsx";
import { Title, TeamSelect, Results, HowTo } from "./ui/Screens.jsx";
import { AccountChip, SignIn, OnlineHub, RoomScreen, NetResults, useCollector, SENT } from "./ui/Online.jsx";
import { HourlyScreen, useCircuitKeeper } from "./ui/Circuit.jsx";
import { awaitSeed } from "./online/stage.js";
import RingsideScreen, { HANDOFF_MS as RING_HANDOFF_MS } from "./ui/RingsideScreen.jsx";
import { createBattle, rivalTeam, step as stepBattle } from "./sim/battle.js";
import { BEING_KIT_VERSION } from "./ieBeingModels.jsx";
import { useOnline, deltaDust, fmtDust, deltaKrests, fmtKrests, serverNow, WIN_PURSE } from "./online/ie.js";
import { createDriver } from "./online/lockstep.js";
import { fbNet } from "./online/net.js";
import { matchPath, joinRoom, leaveRoom, roomPath } from "./online/rooms.js";
import { useHudView, isShown } from "./ui/hudView.js";
// THE MARK + THE FRIENDS LIST (caP'n 2026-10-17: "give Shatterproof a proper The Mark so friends can
// join and we can sit next to each other"). IEFriends.js, Mark.js and MarkEditor.js are the Empire's
// vendored kit — byte-identical, never edited here (PRIME §4 router).
import { IESocialLayer, Z_RAISED } from "./IEFriends";   // its stack as before the 2026-10-21 merge
import Mark from "./Mark";
import MarkEditor from "./MarkEditor";
import { setFriendActions, setInviteOpener } from "./ieSocial";
import { BOX, boxIdentity, boxFight, boxMoveTo, whereIs } from "./online/skybox.js";
// PLAYING TOGETHER (caP'n 2026-10-18): partners are friends on their own; a friend's Mark has a Join bar
import { usePartnerBond } from "./online/partnerBond.js";
import { whereabouts } from "./online/together.js";
import JoinBar from "./ui/JoinBar.jsx";
import { BASE } from "./online/ie.js";
import { SKY, SKY_NET } from "./data/skybox.js";
import { say } from "./data/text.js";
// LYRAEI PLAYS (2026-10-17): her seat, only with ?agent=1 (online/agentSeat.js) — nothing runs without it
import { useAgentSeat } from "./online/agentSeat.js";
// THE CLIMB (2026-10-18, scaffold): a totem up through the Contenders and the Circuit to the League's #1
import ClimbScreen from "./ui/Climb.jsx";
import { collectGuestPay } from "./online/guests.js";
import { useChallengerKeeper } from "./ui/ChallengerStrip.jsx";
import { boutOf, carryOut, finishBout } from "./climb/climb.js";
import { settleBout, beatBout, climbUid, BEAT_MS } from "./climb/climbStore.js";

const PUB = process.env.PUBLIC_URL || "";
const SONG = { title: `${PUB}/audio/bgm_login_menu.mp3`, battle: `${PUB}/audio/bgm_shatterfield.mp3` };
const LS = "shatterproof";
const readLS = () => { try { return JSON.parse(localStorage.getItem(LS) || "{}"); } catch { return {}; } };
const writeLS = (v) => { try { localStorage.setItem(LS, JSON.stringify({ ...readLS(), ...v })); } catch { /* private mode: fine */ } };

// eslint-disable-next-line no-console
console.log(`[shatterproof] being kit ${BEING_KIT_VERSION}`);

export default function App() {
  const [screen, setScreen] = useState("title");
  const [team, setTeam] = useState(() => readLS().team || []);
  const [tomes, setTomes] = useState(() => readLS().tomes || null);
  const [result, setResult] = useState(null);
  const [music, setMusic] = useState(() => readLS().music !== false);
  const [battleKey, setBattleKey] = useState(0);
  const battle = useRef(null);
  const fx = useRef({});
  const paused = useRef(false);
  const userCam = useRef({ yaw: 0, pitch: 0, zoom: 0, camYaw: null });
  const spectate = useRef(false);
  const audio = useRef(null);
  // ── online (Mini Dust, rooms, THE HOURLY) ──────────────────
  const on = useOnline();
  const onRef = useRef(on); onRef.current = on;
  const [toastMsg, setToastMsg] = useState(null);
  const toast = useCallback((m) => { setToastMsg(m); setTimeout(() => setToastMsg((x) => (x === m ? null : x)), 4200); }, []);
  const [roomCode, setRoomCode] = useState(null);
  const [net, setNet] = useState(null);            // { id, from, arena } — the online fight on screen (arena: which field, Arena.jsx arenaFor)
  const [netOver, setNetOver] = useState(false);
  const [afterSignIn, setAfterSignIn] = useState("online");
  const [teamBack, setTeamBack] = useState("title");
  const [ringFight, setRingFight] = useState(null);   // the Hourly fight we're waiting ringside for
  const [ringNonce, setRingNonce] = useState(0);       // a fresh Ringside (so "Sit with them" lands you in the box even if you were already there)
  const climbBout = useRef(null);                      // { uid } while a Climb bout is on screen
  const [climbNews, setClimbNews] = useState(null);
  useCollector(on, useCallback((paid) => {
    const won = paid.reduce((a, p) => a + (p.amount > p.stake ? p.amount - p.stake : 0), 0);
    const back = paid.reduce((a, p) => a + p.amount, 0);
    toast(won ? `Your bets came in: +${fmtDust(won)}` : back ? `Bets settled: ${fmtDust(back)} back` : "Bets settled — not this time");
  }, [toast]));
  // a console handle for testing: __clash.battle, __clash.step(seconds)
  useEffect(() => {
    window.__clash = { get battle() { return battle.current; }, step: (sec = 1) => { for (let i = 0; i < sec * 60; i++) battle.current && stepBattle(battle.current, 1 / 60); } };
  }, []);

  // ── music ──────────────────────────────────────────────────
  useEffect(() => {
    if (!audio.current) { audio.current = new Audio(); audio.current.loop = true; audio.current.volume = 0.55; }
    const a = audio.current;
    const want = screen === "battle" ? SONG.battle : SONG.title;
    if (a.dataset.song !== want) { a.dataset.song = want; a.src = want; }
    if (music) a.play().catch(() => { /* waits for the first click — browsers insist */ });
    else a.pause();
  }, [screen, music]);
  useEffect(() => {
    // the first click anywhere is what browsers need before sound
    const kick = () => { if (music && audio.current && audio.current.paused) audio.current.play().catch(() => {}); };
    window.addEventListener("pointerdown", kick);
    return () => window.removeEventListener("pointerdown", kick);
  }, [music]);
  const toggleMusic = useCallback(() => setMusic((m) => { writeLS({ music: !m }); return !m; }), []);

  // ── THE CLIMB: a bout's result, recorded exactly once, whichever way you leave the fight ──
  // (the end-of-fight timer, ✕ during the last 3 s, Lyraei's leave(), or a new fight starting).
  // A fight that isn't over yet is NOT settled here: the run stays "live" and the Climb screen
  // counts it as lost when you get back there.
  const settleClimb = useCallback((b) => {
    const cb = climbBout.current;
    if (!cb) return false;
    climbBout.current = null;
    if (!b || !b.over || b.winner == null) return false;
    const { uid } = cb, me = onRef.current, carry = carryOut(b, 0), won = b.winner === 0;
    // a server TRANSACTION, and only for the very bout this window claimed (review 2026-10-21: never twice, never the wrong bout)
    let run = null;
    settleBout(uid, cb.mine, (r, rec) => { run = r; return finishBout(r, rec, { won, carry }); }).then((out) => {
      if (!out) return;                                    // another window settled or moved this run on: nothing to record, nothing paid
      const opp = boutOf(run);
      if (out.pay && me && me.writable && me.authUid === uid) deltaKrests(uid, out.pay).then((v) => v != null && toast(`+${fmtKrests(out.pay)} King's Krest${out.pay === 1 ? "" : "s"} for the Climb`));
      const alive = carry.filter((x) => x > 0).length;
      setClimbNews(out.outcome === "next" ? { kind: "next", text: `You beat ${opp.name}. ${alive} of your three walk into bout ${out.run.bout + 1} with the Anima they kept.` }
        : out.outcome === "summit" ? { kind: "summit", text: `You beat ${opp.name}, the League's #1. The summit is yours.` }
        : out.outcome === "rung_cleared" ? { kind: "cleared", text: `${run.title} cleared.${run.replay ? " (A replay: practice only.)" : " The next rung is open."}` }
        : { kind: "run_over", text: `${opp.name} stopped you in bout ${run.bout + 1} of ${run.title}. The run is over.` });
    });
    return true;
  }, [toast]);

  // ── starting a duel ────────────────────────────────────────
  const start = useCallback((myTeam, watch = false, myTomes = null, climb = null) => {
    if (climbBout.current) settleClimb(battle.current);   // a finished Climb bout still waiting to be recorded
    const seed = (Date.now() % 100000) + 1;
    const mine = watch ? rivalTeam(seed + 11) : myTeam;
    const opp = climb ? boutOf(climb) : null;            // a Climb bout: a League NPC with its own three and style, and your carried Anima
    climbBout.current = opp ? { uid: climbUid(onRef.current), mine: { startedAt: climb.startedAt, bout: climb.bout } } : null;
    const myName = (onRef.current && onRef.current.writable && onRef.current.name) || "You";   // the names bar shows YOUR name when you're signed in
    battle.current = createBattle({
      teams: [mine, opp ? opp.team : rivalTeam(seed, watch ? [] : mine)],
      tomes: [watch ? null : myTomes, null],
      seed, auto: [watch, true],
      names: watch ? ["Blue Aenarchist", "Red Aenarchist"] : [myName, opp ? opp.name : "Rival Aenarchist"],
      ...(opp ? { styles: [null, opp.style || null], carry: [climb.carry || null, null] } : {}),
    });
    battle.current.stats = { transcends: 0, clashes: 0, ults: 0, biggest: 0, cursor: 0 };
    fx.current = {};
    paused.current = false;
    userCam.current = { yaw: 0, pitch: 0, zoom: 0, camYaw: null };
    spectate.current = watch;
    setBattleKey((k) => k + 1);
    setScreen("battle");
  }, [settleClimb]);

  // ── watching for the end ───────────────────────────────────
  useEffect(() => {
    if (screen !== "battle") return undefined;
    let endAt = null;
    const id = setInterval(() => {
      const b = battle.current; if (!b) return;
      const st = b.stats;
      for (const e of b.events) {
        if (e.seq <= st.cursor) continue;
        st.cursor = e.seq;
        if (e.type === "transcend") st.transcends++;
        if (e.type === "clash" && e.big) st.clashes++;
        if (e.type === "ult") st.ults++;
        if (e.type === "hit" && !e.construct) st.biggest = Math.max(st.biggest, e.dmg);
      }
      if (b.over && endAt == null) endAt = performance.now() + 3200;   // let the last ruin play out
      if (endAt != null && performance.now() > endAt) {
        clearInterval(id);
        const pick = (s) => ({ name: s.name, anima: s.anima, roster: s.roster.map((r) => ({ idx: r.idx, line: r.line, tier: r.tier, ruined: r.ruined })) });
        const endEv = b.events.find((e) => e.type === "end");
        setResult({ won: b.winner === 0, why: endEv ? endEv.why : "ruined", by: endEv ? endEv.by : null, stats: { ...st, time: b.t }, mine: pick(b.sides[0]), theirs: pick(b.sides[1]), spectate: spectate.current });
        // a win against the AI pays Mini Dust (signed in only; the player's own wallet)
        const me = onRef.current;
        if (!spectate.current && b.winner === 0 && me.writable) deltaDust(me.authUid, WIN_PURSE).then((v) => v != null && toast(`+${fmtDust(WIN_PURSE)} for the win`));
        if (climbBout.current) { settleClimb(b); setScreen("climb"); return; }   // THE CLIMB: the run moves on (or ends), then back to the ladder
        setScreen("results");
      }
    }, 150);
    return () => clearInterval(id);
  }, [screen, battleKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (screen !== "battle" && climbBout.current) settleClimb(battle.current); }, [screen, settleClimb]);
  // a Climb bout being fought says "still here" now and then, so another window knows it isn't a closed one
  useEffect(() => {
    if (screen !== "battle" || !climbBout.current) return undefined;
    const cb = climbBout.current;
    const id = setInterval(() => { if (climbBout.current === cb) beatBout(cb.uid, cb.mine); }, BEAT_MS);
    return () => clearInterval(id);
  }, [screen, battleKey]);

  // ── an ONLINE fight: the lockstep driver owns the battle ──────
  const watchMatch = useCallback(async (id, from) => {
    let spec = await fbNet.get(`${matchPath(id)}/spec`);
    // a League bout's seed lands AT its bell (and its spec once its bell is known): arriving
    // early just waits for it — up to the bell + a little
    const circuit = /^(fn|kr|bt)_/.test(id);
    if (circuit && (!spec || (!spec.void && spec.seed == null))) {
      if (spec && spec.startAt) toast(`Waiting for the bell — ${new Date(spec.startAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`);
      spec = await awaitSeed(fbNet, matchPath(id), serverNow, { until: serverNow() + 30000 });
    }
    if (!spec || spec.seed == null || spec.void) { SENT.delete(id); toast("That fight hasn't started."); return false; }
    if (battle.current && battle.current.driver) battle.current.driver.stop();
    const b = createDriver({ spec, me: on.authUid || null, now: serverNow, path: matchPath(id), net: fbNet });
    b.stats = { transcends: 0, clashes: 0, ults: 0, biggest: 0, cursor: 0 };
    battle.current = b;
    fx.current = {};
    paused.current = false;
    userCam.current = { yaw: 0, pitch: 0, zoom: 0, camYaw: null };
    spectate.current = b.driver.mySide == null;
    setNet({ id, from, arena: arenaFor(spec) });
    setNetOver(false);
    setBattleKey((k) => k + 1);
    setScreen("netbattle");
    return true;   // the Sky Box's "Sit with them" only keeps its move if the fight really opened
  }, [on.authUid, toast]);
  const leaveNet = useCallback(() => {
    const b = battle.current;
    if (b && b.driver) b.driver.leave();
    setScreen(net && net.from === "room" ? "room" : net && net.from === "hourly" ? "hourly" : "online");
  }, [net]);
  useEffect(() => {
    if (screen !== "netbattle") return undefined;
    let endAt = null;
    const id = setInterval(() => {
      const b = battle.current; if (!b) return;
      if (b.over && endAt == null) endAt = performance.now() + 3200;
      if (endAt != null && performance.now() > endAt) { clearInterval(id); setNetOver(true); }
    }, 200);
    return () => clearInterval(id);
  }, [screen, battleKey]);
  useEffect(() => () => { const b = battle.current; if (b && b.driver) b.driver.stop(); }, []);
  // ── THE MARK, FRIENDS AND THE SKY BOX ─────────────────────
  const [markUid, setMarkUid] = useState(null);       // whose Mark is open (THE GLANCE LAW: click a player, see their card)
  const [editMark, setEditMark] = useState(false);
  const [friendsOpen, setFriendsOpen] = useState(false);
  // who I am, for the box (the body defaults from the profile: male → Laostre, female → Lyraei)
  useEffect(() => {
    boxIdentity({ uid: on.authUid || null, name: on.name, writable: !!on.writable, gender: on.profile && on.profile.gender });
  }, [on.authUid, on.name, on.writable, on.profile]);
  // which fight is on screen: an online one (friends can join — or come and watch, if I'm fighting it) / an offline watch (just you) / none
  useEffect(() => {
    if (screen === "netbattle" && net) boxFight(net.id, spectate.current ? {} : { fighting: true, room: net.from === "room" ? roomCode : null });
    else if (screen === "battle" && spectate.current) boxFight(null);
    else if (screen === "ringside" && ringFight) boxFight(ringFight);   // the Sky Box at ringside is that fight's box (it carries into the fight)
    else boxFight(undefined);
  }, [screen, battleKey, net, roomCode, ringFight]);
  usePartnerBond(on);   // mutual partners become friends by themselves (online/partnerBond.js)
  // "Sit with them": find the friend's fight and box, go there, land in the box
  const sitWith = useCallback(async (fUid, fName) => {
    const nm = fName || "Your friend";
    const w = await whereIs(fUid);
    if (!w) { toast(say("sky.notwatching", { name: nm })); return; }
    if (w.f) {   // they're FIGHTING it: no box to sit in — watch it from your own
      if (await watchMatch(w.match, "friend")) toast(say("join.watching", { name: w.name || nm }));
      return;
    }
    const owner = w.box || fUid;
    if (owner !== on.authUid && !owner.startsWith("pub_")) {   // a public box never refuses: it just gets crowded
      // a quick count, so nobody is dropped into a full box
      const rows = (await fbNet.get(`${BASE}/skybox/${owner}`)) || {};
      const now = serverNow(), here = Object.entries(rows).filter(([u, r]) => u !== on.authUid && r && r.m === w.match && now - (r.t || 0) < SKY_NET.stale).length;
      if (here >= SKY.seats) { toast(say("sky.full", { name: nm })); return; }
    }
    const prevBox = BOX.box;
    const prevHost = BOX.hostUid;
    boxMoveTo(owner === on.authUid ? null : owner, fUid);
    BOX.wantView = "sky";
    // a League bout still before its bell: their box is at RINGSIDE (watchMatch would sit waiting for the bell)
    if (/^bt_/.test(w.match)) {
      const spec = await fbNet.get(`${matchPath(w.match)}/spec`).catch(() => null);
      if (!spec || (!spec.void && spec.seed == null && (spec.startAt == null || spec.startAt - serverNow() > RING_HANDOFF_MS))) {
        const cur = battle.current; if (cur && cur.driver) cur.driver.leave();   // leave a fight you were watching first
        setRingFight(w.match); setRingNonce((n) => n + 1); setScreen("ringside");
        toast(say("sky.joined", { name: w.name || nm }));
        return;
      }
    }
    if (await watchMatch(w.match, "hourly")) toast(say("sky.joined", { name: w.name || nm }));
    else { BOX.wantView = null; boxMoveTo(prevBox, prevHost); }   // the fight didn't open: nothing about you moved
  }, [on.authUid, toast, watchMatch]);
  const roomCodeRef = useRef(null); roomCodeRef.current = roomCode;
  const screenRef = useRef(screen); screenRef.current = screen;
  const [boxWaiting, setBoxWaiting] = useState(false);   // Lyraei waiting for her partner: her presence says so
  // ⭐ JOIN A FRIEND, wherever they are (the Join bar under their Mark, the friends list's ⋯, a CALL, Lyraei's seat):
  // a room → join it (a fight already going there sends you in to watch, like everyone in the room) · their own
  // online fight → watch it · watching / waiting in a Sky Box → sit with them
  const joinFriend = useCallback(async (fUid, fName) => {
    const nm = fName || "Your friend";
    if (fightingRef.current) { toast(say("sky.busy")); return false; }   // never pull a fighter out of their own fight
    const w = await whereabouts(fUid);
    if (w.kind === "room" || w.kind === "roomfight") {
      if (roomCodeRef.current === w.room && screenRef.current === "room") return true;   // already there
      const mine = roomCodeRef.current;
      if (mine && mine !== w.room) {   // leave my own room properly first (a host hands it on — review 2026-10-18)
        const old = await fbNet.get(roomPath(mine)).catch(() => null);
        await leaveRoom(fbNet, { code: mine, uid: on.authUid, room: old }).catch(() => {});
      }
      const r = await joinRoom(fbNet, { code: w.room, uid: on.authUid, name: on.name });
      if (!r.ok) { toast(r.reason || say("join.gone", { name: nm })); return false; }
      const b = battle.current;
      if (screenRef.current === "netbattle" && b && b.driver) b.driver.leave();   // watching something else: leave it properly first
      setRoomCode(r.code); setScreen("room");
      toast(say(w.kind === "roomfight" ? "join.watching" : "join.joined.room", { name: nm }));
      return true;
    }
    if (w.kind === "fight" || w.kind === "watch" || w.kind === "wait") { await sitWith(fUid, nm); return true; }
    toast(say("join.nothing", { name: nm }));
    return false;
  }, [on.authUid, on.name, toast, sitWith]);
  // what's open over the fight (the Esc-to-leave and the Sky Box keys stand down under these)
  const overlay = !!(markUid || editMark || friendsOpen);
  const overlayRef = useRef(false); overlayRef.current = overlay;
  BOX.ui.modal = overlay;
  BOX.ui.friendsReady = !!(on.writable && on.phase === "ready" && on.authUid);
  const fightingRef = useRef(false); fightingRef.current = (screen === "netbattle" || screen === "battle") && !spectate.current;   // practice / a Climb bout too (review 2026-10-18)
  useEffect(() => {
    BOX.ui.toast = toast;
    BOX.ui.openMark = (uid) => setMarkUid(uid);
    BOX.ui.openFriends = () => setFriendsOpen(true);
    // follow the friend whose box I'm in: to their new box on this fight, or to their next fight and THEN their box there
    BOX.ui.follow = async (host) => {
      const hu = BOX.hostUid;
      if (host.match === BOX.match) { if (host.box) boxMoveTo(host.box, hu); return; }
      BOX.wantView = "sky";
      if (!(await watchMatch(host.match, "hourly")) || !hu) return;
      const w = await whereIs(hu);
      if (w && !w.f && w.match === host.match) boxMoveTo(w.box || hu, hu);
    };
    BOX.ui.onWaiting = setBoxWaiting;
    // the friends panel's own verbs: a friend in Shatterproof → "Join them" (their room, their fight, or their box)
    setFriendActions((f) => (f && f.online && f.game && f.game.id === "shatterproof" ? [{ label: say("join.them"), run: () => joinFriend(f.uid, f.name) }] : []));
    // a CALL from a friend while we're both in Shatterproof: go to them, never a page reload
    setInviteOpener((inv) => {
      if (!inv || inv.gameId !== "shatterproof") return false;
      if (fightingRef.current) { toast(say("sky.busy")); return true; }   // never pull a fighter out of their own fight
      joinFriend(inv.fromUid, inv.fromName); return true;
    });
    return () => { setFriendActions(null); setInviteOpener(null); BOX.ui.onWaiting = null; };
  }, [toast, watchMatch, sitWith, joinFriend]);
  // ⭐ THE AGENT SEAT (Lyraei Plays): App hands her seat what it knows, every render (it reads a ref)
  useAgentSeat({ screen, setScreen, on, battle, team, setTeam, tomes, setTomes, start, watchMatch, leaveNet, roomCode, setRoomCode,
    result, sitWith, joinFriend, spectating: () => spectate.current, markOpen: !!markUid || editMark });
  const inFight = screen === "battle" || screen === "netbattle";
  const zone = inFight ? (spectate.current ? (boxWaiting ? say("zone.waiting") : "Watching a fight") : "Fighting") : screen === "hourly" || screen === "ringside" ? "The League" : null;

  // THE KEEPER: every signed-in screen fights the Circuit's unwatched fights, a slice at a time (tiny slices mid-fight)
  useCircuitKeeper(on, screen === "netbattle" || screen === "battle");
  // CHALLENGERS (2026-10-21): "I'm in" kept fresh while it's on, and my League wins paid in King's Krests (my wallet only)
  useChallengerKeeper(on, team, tomes, toast);
  // GUESTS (retired 2026-10-21): a guest's win in the Circuit still owed pays King's Krests — collected by my own screen, now and then
  useEffect(() => {
    if (!on.writable || !on.authUid) return undefined;
    const uid = on.authUid;
    let alive = true, running = false;
    const go = async () => {
      if (running || !alive) return; running = true;
      try {
        const got = await collectGuestPay(fbNet, uid, serverNow(), (n) => deltaKrests(uid, n));
        if (got && alive) toast(`+${fmtKrests(got)} King's Krest${got === 1 ? "" : "s"} for your win in the Circuit`);
      } catch { /* next pass */ } finally { running = false; }
    };
    const first = setTimeout(go, 5000), id = setInterval(go, 60000);
    return () => { alive = false; clearTimeout(first); clearInterval(id); };
  }, [on.writable, on.authUid, toast]);

  useEffect(() => {
    if (!(screen === "battle" || screen === "netbattle") || !spectate.current) return undefined;
    const esc = (e) => { const t = e.target; if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;   /* typing in the chat box: Esc is the box's */
      if (overlayRef.current) { if (e.key === "Escape") { setFriendsOpen(false); } return; }   /* a Mark or the friends panel is open: Esc closes that, never the fight */
      if (e.key === "Escape") { if (screen === "netbattle") leaveNet(); else setScreen("title"); } };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [screen, leaveNet]);

  const goOnline = () => setScreen("online");
  const pickTeam = (back) => { setTeamBack(back); setScreen("team"); };
  const nb = battle.current;
  const netSide = nb && nb.driver ? (nb.driver.mySide == null ? 0 : nb.driver.mySide) : 0;

  return (
    <div className="app">
      {screen === "title" && <Title onPlay={() => pickTeam("title")} onWatch={() => start(null, true)} onHow={() => setScreen("how")} onOnline={goOnline} onClimb={() => { setClimbNews(null); setScreen("climb"); }} />}
      {screen === "climb" && <ClimbScreen on={on} team={team} tomes={tomes} news={climbNews} onNews={setClimbNews}
        onFight={(run) => start(run.team || team, false, run.tomes || tomes, run)} onPickTeam={() => pickTeam("climb")} onBack={() => setScreen("title")} />}
      {["title", "online", "room", "hourly", "results", "climb"].includes(screen) && (
        <div className="on-corner"><AccountChip on={on} onOpen={() => { if (!on.writable) { setAfterSignIn(screen); setScreen("signin"); } else setMarkUid(on.authUid); }} /></div>
      )}
      {screen === "signin" && <SignIn on={on} onDone={() => setScreen(afterSignIn || "online")} onBack={() => setScreen(afterSignIn || "title")} />}
      {screen === "online" && <OnlineHub on={on} toast={toast} onBack={() => setScreen("title")} onSignIn={() => { setAfterSignIn("online"); setScreen("signin"); }}
        onRoom={(c) => { setRoomCode(c); setScreen("room"); }} onHourly={() => setScreen("hourly")} />}
      {screen === "room" && roomCode && <RoomScreen on={on} code={roomCode} team={team} tomes={tomes} onPickTeam={() => pickTeam("room")}
        onWatch={(id) => watchMatch(id, "room")} onBack={() => { setRoomCode(null); setScreen("online"); }} />}
      {screen === "hourly" && <HourlyScreen on={on} team={team} tomes={tomes} onPickTeam={() => pickTeam("hourly")} toast={toast}
        onWatch={(id) => watchMatch(id, "hourly")} onRingside={(id) => { setRingFight(id); setScreen("ringside"); }} onBack={() => setScreen("online")} />}
      {screen === "ringside" && ringFight && <RingsideScreen key={`${ringFight}_${ringNonce}`} on={on} fightId={ringFight}
        onWatch={(id) => watchMatch(id, "hourly")} onBack={() => setScreen("hourly")} />}
      {screen === "how" && <HowTo onBack={() => setScreen("title")} />}
      {screen === "team" && (
        <TeamSelect initial={team} initialTomes={tomes || undefined} onBack={() => setScreen(teamBack)}
          goLabel={teamBack === "title" ? null : "Keep these three ▸"}
          onStart={(t, tm) => { setTeam(t); setTomes(tm); writeLS({ team: t, tomes: tm }); if (teamBack === "title") start(t, false, tm); else setScreen(teamBack); }} />
      )}
      {screen === "battle" && battle.current && (
        <div className="battle" key={battleKey}>
          <BattleScene battle={battle} fx={fx.current} paused={paused} userCam={userCam} mySide={0} spectate={spectate.current} />
          <Hud battle={battle} mySide={0} paused={paused} userCam={userCam} spectate={spectate.current} on={on}
            onExit={() => { const c = !!climbBout.current; if (c) settleClimb(battle.current); setScreen(c ? "climb" : "title"); }} music={music} toggleMusic={toggleMusic} />
        </div>
      )}
      {screen === "netbattle" && battle.current && (
        <div className="battle" key={battleKey}>
          <BattleScene battle={battle} fx={fx.current} paused={paused} userCam={userCam} mySide={netSide} spectate={spectate.current} arena={(net && net.arena) || "shard"} />
          <Hud battle={battle} mySide={netSide} paused={paused} userCam={userCam} spectate={spectate.current} on={on}
            onExit={leaveNet} music={music} toggleMusic={toggleMusic} />
          <Countdown b={nb} />
          <NetStatus b={nb} />
          {netOver && <NetResults b={battle.current} backLabel={net && net.from === "room" ? "Back to the room" : net && net.from === "hourly" ? "Back to the board" : "Back"} onBack={leaveNet} />}
        </div>
      )}
      {toastMsg && <div className="on-toast">{toastMsg}</div>}
      {/* THE MARK'S SOCIAL LAYER: the friends pill (menus) / panel (the Sky Box's ＋ Friends), invites, presence */}
      {on.writable && on.phase === "ready" && on.authUid && (
        <IESocialLayer uid={on.authUid} name={on.name} game="shatterproof" zone={zone} partyId={on.partyId} z={Z_RAISED}
          accent="#7ce7ff" MarkView={Mark} open={friendsOpen} onOpenChange={setFriendsOpen}
          hidePill={inFight} bottom={inFight ? 64 : null} />
      )}
      {markUid && <Mark uid={markUid} viewingSelf={markUid === on.authUid} onClose={() => setMarkUid(null)} onEdit={() => setEditMark(true)} />}
      {markUid && markUid !== on.authUid && !editMark && BOX.ui.friendsReady && (
        <JoinBar uid={markUid} me={on.authUid} myName={on.name} toast={toast} onJoin={(u, n) => { setMarkUid(null); joinFriend(u, n); }} />
      )}
      {editMark && on.authUid && <MarkEditor uid={on.authUid} onClose={() => setEditMark(false)} />}
      {screen === "results" && result && (
        <Results result={result}
          onRematch={() => start(team, result.spectate, tomes)}
          onTeam={() => pickTeam("title")}
          onTitle={() => setScreen("title")} />
      )}
    </div>
  );
}

// the bell's countdown over an online fight (its own tick: App doesn't re-render mid-fight)
function Countdown({ b }) {
  const [, set] = useState(0);
  const left = b && b.driver ? b.driver.spec.startAt - serverNow() : 0;
  const going = left > 0;
  useEffect(() => { if (!going) return undefined; const id = setInterval(() => set((n) => n + 1), 250); return () => clearInterval(id); }, [going]);
  if (!going) return null;
  return <div className="net-count">{Math.ceil(left / 1000)}<small>{b.driver.spec.names[0]} vs {b.driver.spec.names[1]}</small></div>;
}

// a small line over an online fight: waiting on someone, out of sync, the AI took a side
function NetStatus({ b }) {
  const [, set] = useState(0);
  useEffect(() => { const id = setInterval(() => set((n) => n + 1), 500); return () => clearInterval(id); }, []);
  const view = useHudView();
  if (!b || !b.driver || !isShown(view, "status")) return null;   // "Status notes" can be hidden like any other piece
  const st = b.driver.state, names = b.driver.spec.names;
  const lines = [];
  if (st.stalled && st.waitingOn != null) lines.push(`Waiting for ${names[st.waitingOn]}…`);
  [0, 1].forEach((s) => { if (st.takenOver[s]) lines.push(`${names[s]} left — the AI fights for them`); });
  if (st.lostControl) lines.push("Your screen slept too long — the AI took your side");
  if (st.desync) lines.push("⚠ out of sync with the other screens");
  return lines.length ? <div className="net-status">{lines.map((l) => <div key={l}>{l}</div>)}</div> : null;
}

